import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  FaChevronLeft,
  FaChevronRight,
  FaExternalLinkAlt,
  FaFileAlt,
  FaFileContract,
  FaFileInvoiceDollar,
  FaFolderOpen,
  FaTimes,
  FaUpload,
} from "react-icons/fa";
import { getPermissions } from "../../../utilis/permissionStorage";
import { apiUrl } from "../../ApiManager/AxiosHeaders";
import ClientDocumentUploadPanel from "../../Applications/ClientDocumentUploadPanel";
import RecategorizeCategoryControl from "../../Clients/RecategorizeCategoryControl";
import {
  fetchCommissionApplicationDocuments,
  getDocumentLabel,
  getDocumentPreviewKind,
  getDocumentUrl,
  splitClientDocuments,
} from "./clientDocumentsUtils";

function getDocumentKey(document = {}) {
  const type = String(document?.type || "").trim().toLowerCase();
  const id = document.id ?? getDocumentLabel(document);
  if (type === "provider_contract") {
    return `provider-contract-${id}`;
  }
  return String(id);
}

function isProviderContract(document = {}) {
  return (
    String(document?.type || "").trim().toLowerCase() === "provider_contract"
  );
}

function resolveRecategorizeCategory(activeCategory) {
  return activeCategory === "finance" ? "finance" : "general";
}

function DocumentPreviewPanel({ document }) {
  const url = getDocumentUrl(document);
  const label = getDocumentLabel(document);
  const kind = getDocumentPreviewKind(document);

  if (!url) {
    return (
      <div className="flex h-full flex-col items-center justify-center bg-slate-50 px-6 text-center">
        <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-white shadow-sm ring-1 ring-slate-200">
          <FaFileAlt size={20} className="text-slate-300" />
        </div>
        <p className="mb-0 text-sm font-semibold text-slate-700">{label}</p>
        <p className="mb-0 mt-1 text-[12px] text-slate-500">Preview unavailable.</p>
      </div>
    );
  }

  if (kind === "image") {
    return (
      <div className="flex h-full items-center justify-center overflow-auto bg-slate-50 p-4">
        <img
          src={url}
          alt={label}
          className="max-h-full max-w-full rounded-md object-contain shadow-sm"
        />
      </div>
    );
  }

  return (
    <iframe
      title={label}
      src={url}
      className="h-full w-full border-0 bg-slate-100"
    />
  );
}

function DocumentListItem({
  doc,
  isSelected,
  onSelect,
  showRowActions = false,
  listCategory = "general",
  clientId,
  appId,
  appType,
  hasPermissionPageAccess,
  onRecategorized,
}) {
  const label = getDocumentLabel(doc);
  const docKey = getDocumentKey(doc);

  const rowShell = isSelected
    ? "bg-[#007BFF] text-white shadow-sm ring-1 ring-[#007BFF]/30"
    : "text-slate-700 hover:bg-white hover:shadow-sm ring-1 ring-transparent hover:ring-slate-200/80";

  const actionToneClass = isSelected ? "is-selected-tone" : "";

  return (
    <li className="group">
      <div
        className={`flex items-center gap-1 rounded-lg px-1.5 py-1 transition ${rowShell}`}
      >
        <button
          type="button"
          onClick={() => onSelect(docKey)}
          className="flex min-w-0 flex-1 items-center gap-2 py-0.5 text-left"
          style={{ boxShadow: "none", border: "none", background: "transparent" }}
          title={label}
        >
          <FaFileAlt
            size={12}
            className={`shrink-0 ${isSelected ? "text-white/90" : "text-slate-400"}`}
          />
          <span
            className={`block min-w-0 flex-1 truncate text-[11px] font-semibold leading-tight ${
              isSelected ? "text-white" : "text-slate-800"
            }`}
          >
            {label}
          </span>
        </button>

        {showRowActions && hasPermissionPageAccess ? (
          <div
            className="flex shrink-0 items-center gap-0.5 pr-0.5"
            onClick={(event) => event.stopPropagation()}
          >
            <RecategorizeCategoryControl
              itemType="document"
              item={doc}
              clientId={clientId}
              currentCategory={resolveRecategorizeCategory(listCategory)}
              appId={appId}
              appType={appType}
              variant="icon"
              className={actionToneClass}
              onSuccess={onRecategorized}
            />
          </div>
        ) : null}
      </div>
    </li>
  );
}

function ListPanelToggle({ listOpen, onToggle, className = "" }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      title={listOpen ? "Hide document list" : "Show document list"}
      className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 transition hover:border-[#007BFF]/35 hover:bg-[#F7FAFF] hover:text-[#007BFF] ${className}`}
      style={{ boxShadow: "none" }}
      aria-label={listOpen ? "Hide document list" : "Show document list"}
      aria-expanded={listOpen}
    >
      {listOpen ? <FaChevronLeft size={10} /> : <FaChevronRight size={10} />}
    </button>
  );
}

const CATEGORY_CONFIG = [
  {
    id: "general",
    label: "General",
    icon: FaFolderOpen,
    emptyLabel: "No general documents",
  },
  {
    id: "finance",
    label: "Finance",
    icon: FaFileInvoiceDollar,
    emptyLabel: "No finance documents",
  },
  {
    id: "provider",
    label: "Contracts",
    icon: FaFileContract,
    emptyLabel: "No provider contracts",
  },
];

function ClientDocumentsModal({
  open,
  clientName,
  documents,
  onClose,
  title = "Client documents",
  sidebarLabel = "Client documents",
  headerActions = null,
  contractsOnly = false,
  clientId = null,
  providerId = null,
  appId = null,
  appType = null,
  onDocumentsChange = null,
  allowUpload = true,
}) {
  const manageMode = Boolean(clientId);
  const canUpload = manageMode && allowUpload;
  const [panel, setPanel] = useState("browse");
  const [documentsLoading, setDocumentsLoading] = useState(false);

  const hasPermissionPageAccess = useMemo(() => {
    const perms = getPermissions();
    return perms?.settings_page === "1" || perms?.settings_page === 1;
  }, []);

  const refreshDocuments = useCallback(async () => {
    if (!manageMode) return documents || [];
    setDocumentsLoading(true);
    try {
      const next = await fetchCommissionApplicationDocuments({
        clientId,
        providerId,
        apiUrl,
      });
      if (onDocumentsChange) {
        await Promise.resolve(onDocumentsChange(next));
      }
      return next;
    } finally {
      setDocumentsLoading(false);
    }
  }, [manageMode, clientId, providerId, onDocumentsChange]);

  const displayDocuments = documents || [];

  const { general, finance, providerContracts } = useMemo(
    () => splitClientDocuments(displayDocuments),
    [displayDocuments],
  );

  const documentsByCategory = useMemo(
    () => ({
      general,
      finance,
      provider: providerContracts,
    }),
    [general, finance, providerContracts],
  );

  const visibleCategoryConfig = useMemo(
    () => (
      contractsOnly
        ? CATEGORY_CONFIG.filter((cat) => cat.id === "provider")
        : CATEGORY_CONFIG
    ),
    [contractsOnly],
  );

  const totalCount = contractsOnly
    ? providerContracts.length
    : general.length + finance.length + providerContracts.length;

  const [listOpen, setListOpen] = useState(true);
  const [activeCategory, setActiveCategory] = useState(
    contractsOnly ? "provider" : "general",
  );
  const [selectedId, setSelectedId] = useState(null);

  const categoryTabs = useMemo(
    () =>
      visibleCategoryConfig.map((cat) => ({
        ...cat,
        documents: documentsByCategory[cat.id] || [],
        count: (documentsByCategory[cat.id] || []).length,
      })),
    [documentsByCategory, visibleCategoryConfig],
  );

  const activeTab =
    categoryTabs.find((tab) => tab.id === activeCategory) || categoryTabs[0];

  const allDocuments = useMemo(
    () => (
      contractsOnly
        ? providerContracts
        : [...general, ...finance, ...providerContracts]
    ),
    [contractsOnly, general, finance, providerContracts],
  );

  const selectedDocument =
    allDocuments.find((doc) => getDocumentKey(doc) === String(selectedId)) ||
    null;

  const previewUrl = selectedDocument ? getDocumentUrl(selectedDocument) : "";
  const previewLabel = selectedDocument ? getDocumentLabel(selectedDocument) : "";
  const categoryLabel = activeTab?.label || "Documents";

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !manageMode) return;
    refreshDocuments();
    setPanel("browse");
  }, [open, manageMode, clientId, providerId]);

  useEffect(() => {
    if (!open) return;
    setListOpen(true);

    const defaultCategory = contractsOnly ? "provider" : "general";
    const firstCategory =
      categoryTabs.find((tab) => tab.count > 0)?.id || defaultCategory;
    setActiveCategory(firstCategory);

    const firstDoc = contractsOnly
      ? providerContracts[0] || null
      : documentsByCategory[firstCategory]?.[0]
        || general[0]
        || finance[0]
        || providerContracts[0]
        || null;

    setSelectedId(firstDoc ? getDocumentKey(firstDoc) : null);
  }, [
    open,
    contractsOnly,
    categoryTabs,
    documentsByCategory,
    general,
    finance,
    providerContracts,
    displayDocuments,
  ]);

  const handleCategoryChange = (categoryId) => {
    setActiveCategory(categoryId);
    const firstInCategory = documentsByCategory[categoryId]?.[0];
    if (firstInCategory) {
      setSelectedId(getDocumentKey(firstInCategory));
    }
  };

  const handleSelectDocument = (docId) => {
    setSelectedId(docId);
    if (window.matchMedia("(max-width: 767px)").matches) {
      setListOpen(false);
    }
  };

  const handleRecategorized = async (targetCategory) => {
    const refreshed = await refreshDocuments();
    const list = refreshed || displayDocuments;
    const split = splitClientDocuments(list);
    const nextCategory =
      targetCategory === "finance" || targetCategory === "financial"
        ? "finance"
        : "general";
    setActiveCategory(nextCategory);
    const first = split[nextCategory === "finance" ? "finance" : "general"]?.[0];
    if (first) {
      setSelectedId(getDocumentKey(first));
    }
  };

  const handleUploadSuccess = async () => {
    await refreshDocuments();
    setPanel("browse");
  };

  if (!open) return null;

  const headerSubtitle =
    panel === "upload"
      ? "Upload new document"
      : selectedDocument
        ? `${previewLabel} · ${categoryLabel}`
        : `${clientName || "Client"} · ${totalCount} ${
            totalCount === 1 ? "file" : "files"
          }`;

  const showListRowActions =
    manageMode && panel === "browse" && activeCategory !== "provider";

  const HeaderIcon = contractsOnly ? FaFileContract : FaFolderOpen;
  const emptyTitle = contractsOnly
    ? "No provider contracts"
    : "No documents uploaded";
  const emptyHint = contractsOnly
    ? "Upload a contract using Upload Document."
    : "Files will appear here when added.";

  return createPortal(
    <>
      <div
        className="cms-app-modal-backdrop bg-black/45"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className="cms-app-modal cms-app-modal--centered flex items-center justify-center p-2 sm:p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="documents-modal-title"
        onClick={onClose}
      >
        <div
          className="tailwind-container relative z-10 flex h-[min(90vh,780px)] w-full max-w-6xl flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl"
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <div className="flex shrink-0 items-center gap-3 border-b border-slate-100 bg-gradient-to-r from-[#F7FAFF] to-white px-4 py-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#007BFF] text-white shadow-sm">
              <HeaderIcon size={14} />
            </div>

            <div className="min-w-0 flex-1">
              <h2
                id="documents-modal-title"
                className="mb-0 text-[15px] font-semibold text-slate-900"
              >
                {title}
              </h2>
              <p className="mb-0 truncate text-[11px] text-slate-500">
                {headerSubtitle}
              </p>
            </div>

            <div className="flex shrink-0 items-center gap-1.5">
              {headerActions}
              {canUpload && panel === "browse" ? (
                <button
                  type="button"
                  onClick={() => setPanel("upload")}
                  className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-[#007BFF] px-3 text-[11px] font-semibold text-white shadow-sm transition hover:bg-[#0069D9]"
                  style={{ border: "none" }}
                  title="Upload a new client document"
                >
                  <FaUpload size={11} />
                  Upload
                </button>
              ) : null}
              {canUpload && panel === "upload" ? (
                <button
                  type="button"
                  onClick={() => setPanel("browse")}
                  className="inline-flex h-8 items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 text-[11px] font-semibold text-slate-600 transition hover:border-[#007BFF]/30 hover:text-[#007BFF]"
                  style={{ boxShadow: "none" }}
                  title="Return to document list"
                >
                  <FaChevronLeft size={10} />
                  Back
                </button>
              ) : null}
              {panel === "browse" && previewUrl ? (
                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 text-[11px] font-semibold text-slate-600 transition hover:border-[#007BFF]/30 hover:text-[#007BFF]"
                  onClick={(event) => event.stopPropagation()}
                  title="Open file in a new tab"
                >
                  <FaExternalLinkAlt size={10} />
                  <span className="hidden sm:inline">Open</span>
                </a>
              ) : null}
              <button
                type="button"
                onClick={onClose}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition hover:bg-slate-50"
                style={{ boxShadow: "none" }}
                aria-label="Close"
              >
                <FaTimes size={12} />
              </button>
            </div>
          </div>

          {panel === "upload" && canUpload ? (
            <ClientDocumentUploadPanel
              clientId={clientId}
              onSuccess={handleUploadSuccess}
              variant="modal"
              showTitle={false}
              inputIdPrefix="commissionClientDoc"
            />
          ) : (
            <div className="relative flex min-h-0 flex-1 bg-slate-50/40">
              <aside
                className={`flex shrink-0 flex-col overflow-hidden border-slate-200 bg-slate-50 transition-[width] duration-200 ease-out ${
                  listOpen ? "w-[300px] border-r sm:w-[340px]" : "w-0 border-r-0"
                }`}
                aria-hidden={!listOpen}
              >
                <div className="flex min-h-0 w-[300px] flex-col sm:w-[340px]">
                  {documentsLoading && totalCount === 0 ? (
                    <div className="flex flex-1 flex-col items-center justify-center px-6 py-10 text-center">
                      <p className="mb-0 text-[12px] text-slate-500">
                        Loading documents…
                      </p>
                    </div>
                  ) : totalCount === 0 ? (
                    <div className="flex flex-1 flex-col items-center justify-center px-6 py-10 text-center">
                      <HeaderIcon size={22} className="text-slate-300" />
                      <p className="mb-0 mt-3 text-[13px] font-semibold text-slate-700">
                        {emptyTitle}
                      </p>
                      <p className="mb-0 mt-1 text-[11px] text-slate-400">
                        {emptyHint}
                      </p>
                      {canUpload ? (
                        <button
                          type="button"
                          onClick={() => setPanel("upload")}
                          className="mt-4 inline-flex h-9 items-center gap-1.5 rounded-lg bg-[#007BFF] px-4 text-[11px] font-semibold text-white shadow-sm hover:bg-[#0069D9]"
                          style={{ border: "none" }}
                          title="Upload your first document"
                        >
                          <FaUpload size={11} />
                          Upload document
                        </button>
                      ) : null}
                    </div>
                  ) : (
                    <>
                      <div className="shrink-0 border-b border-slate-200/80 px-3 pb-2 pt-3">
                        <div className="mb-2 flex items-center justify-between gap-2">
                          <p className="mb-0 text-[11px] font-semibold text-slate-600">
                            {sidebarLabel}
                          </p>
                          <ListPanelToggle
                            listOpen={listOpen}
                            onToggle={() => setListOpen(false)}
                          />
                        </div>
                        {categoryTabs.length > 1 ? (
                        <div className="grid grid-cols-3 gap-1 rounded-lg bg-slate-200/60 p-1">
                          {categoryTabs.map((tab) => {
                            const Icon = tab.icon;
                            const isActive = activeCategory === tab.id;
                            return (
                              <button
                                key={tab.id}
                                type="button"
                                onClick={() => handleCategoryChange(tab.id)}
                                className={`flex flex-col items-center gap-0.5 rounded-md px-1 py-1.5 text-center transition ${
                                  isActive
                                    ? "bg-white text-[#007BFF] shadow-sm"
                                    : "text-slate-500 hover:text-slate-700"
                                }`}
                                style={{ border: "none" }}
                              >
                                <Icon size={11} />
                                <span className="text-[10px] font-semibold leading-none">
                                  {tab.label}
                                </span>
                                <span
                                  className={`text-[9px] font-bold tabular-nums ${
                                    isActive
                                      ? "text-[#007BFF]/80"
                                      : "text-slate-400"
                                  }`}
                                >
                                  {tab.count}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                        ) : null}
                      </div>

                      <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
                        {activeTab.documents.length === 0 ? (
                          <p className="mb-0 px-2 py-8 text-center text-[12px] text-slate-400">
                            {activeTab.emptyLabel}
                          </p>
                        ) : (
                          <ul className="space-y-0.5">
                            {activeTab.documents.map((doc) => (
                              <DocumentListItem
                                key={getDocumentKey(doc)}
                                doc={doc}
                                isSelected={
                                  getDocumentKey(doc) === String(selectedId)
                                }
                                onSelect={handleSelectDocument}
                                showRowActions={
                                  showListRowActions &&
                                  !isProviderContract(doc)
                                }
                                listCategory={activeCategory}
                                clientId={clientId}
                                appId={appId}
                                appType={appType}
                                hasPermissionPageAccess={hasPermissionPageAccess}
                                onRecategorized={handleRecategorized}
                              />
                            ))}
                          </ul>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </aside>

              <section className="flex min-w-0 flex-1 flex-col bg-white">
                {!listOpen && totalCount > 0 ? (
                  <div className="flex shrink-0 items-center justify-between gap-2 border-b border-slate-100 bg-slate-50 px-3 py-2.5">
                    <p className="mb-0 text-[11px] font-semibold text-slate-600">
                      {sidebarLabel}
                    </p>
                    <ListPanelToggle
                      listOpen={listOpen}
                      onToggle={() => setListOpen(true)}
                    />
                  </div>
                ) : null}
                <div className="min-h-0 flex-1">
                  {selectedDocument ? (
                    <DocumentPreviewPanel document={selectedDocument} />
                  ) : (
                    <div className="flex h-full flex-col items-center justify-center bg-slate-50 px-6 text-center">
                      <FaFileAlt size={24} className="text-slate-300" />
                      <p className="mb-0 mt-3 text-[13px] font-medium text-slate-600">
                        Select a document to preview
                      </p>
                      <p className="mb-0 mt-1 text-[11px] text-slate-400">
                        Use the list on the left to browse files
                      </p>
                    </div>
                  )}
                </div>
              </section>
            </div>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
}

export default ClientDocumentsModal;
